/**
 * /guides/how-many-people-wedding-table
 *
 * "How Many People Should Sit at a Wedding Table?" — the second production
 * guide, and the capacity question in the wedding-seating cluster. The first
 * guide (/guides/wedding-seating-plan) answers "who sits where"; this one
 * answers the question that comes before it, and hands back over once the
 * reader has a number.
 *
 * The article is entirely useful without Agapaé. Its whole argument is that a
 * seating number is a starting point rather than a rule, and that the venue's
 * or rental company's specification for the actual tables is the authority —
 * which is the opposite of a calculator landing page, and deliberately so.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * One, and only one:
 *
 *   "Agapaé's seating tools are available on the Heirloom plan."
 *
 * It appears once, in the closing section, as a note beside the link to
 * /wedding-seating — the page where the capability is actually described. This
 * guide describes NO product behaviour of its own, so the four prohibitions
 * that govern the first guide are joined here by a fifth: do not describe what
 * the seating tools do. Nothing in this file may imply drag-and-drop, a visual
 * floor plan, automatic table generation, any automatic seating algorithm,
 * automatic reassignment or removal of guests, or guest access to seating.
 *
 * ── The numbers ───────────────────────────────────────────────────────────
 * Every capacity figure in section 3 is framed as a planning range, twice: in
 * the sentence introducing it and in the sentence after it. Neither the copy
 * nor a future edit may present one as an industry standard — there isn't one,
 * and the venue's own specification is what the reader is told to use. Keep
 * the "comfortable starting point" / "possible maximum" pair intact; the
 * distinction between them is the point of the whole article.
 *
 * ── One structural note ───────────────────────────────────────────────────
 * The round-table figures are a `list`, not a table. `BLOCK_TYPES` has no
 * `table` — adding one would mean teaching both renderers, the model and the
 * CSS about it, which is infrastructure work this content change has no reason
 * to do. Each item carries its diameter and both column values verbatim, so no
 * figure and no column label is lost.
 */

const howManyPeopleWeddingTableEn = {
  slug: "how-many-people-wedding-table",

  title: "How Many People Should Sit at a Wedding Table?",
  metaTitle: "How Many People Should Sit at a Wedding Table? | Agapaé",
  metaDescription:
    "Learn how many guests to seat at a wedding table, how table size affects comfort, and what to consider before deciding your reception layout.",

  eyebrow: "Wedding seating",

  hero: {
    src: "/assets/images/moments/moment-3.jpg",
    // Describes the photograph, not the keyword: a black and white frame of a
    // couple standing in a sunlit meadow. There is no table in it, and saying
    // otherwise would be a lie told to exactly the readers who cannot see it.
    alt: "A black and white photograph of a bride and groom standing close together in a sunlit meadow, foreheads almost touching as they hold hands",
    width: 6983,
    height: 3436,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `There is no single number that works for every wedding table.`,
    `A table may technically fit a certain number of chairs, but that does not necessarily mean it is comfortable for a full meal. The right number depends on the table's size and shape, the chairs, the place settings, the meal style, and how much space your guests need to sit, eat, and talk comfortably.`,
    `For many weddings, 8 to 10 guests per round table is a useful starting point. But it should remain a starting point, not a rule.`,
    `Before deciding how many people to place at each table, check what your venue actually provides and how the tables will be arranged in the reception room.`,
  ],

  sections: [
    {
      heading: "1. Start With the Table Your Venue Actually Provides",
      content: [
        `The first question is not "How many people should sit at a wedding table?"`,
        `It is:`,
        `"What tables will we actually have?"`,
        `Wedding venues and rental companies may use tables of different sizes and shapes. A round table's diameter makes a significant difference to how many guests can sit around it comfortably. Rectangular and banquet tables work differently again.`,
        `If your venue has already specified the table dimensions, use those measurements as your starting point rather than choosing a capacity from a generic seating chart.`,
        `Also ask about the chairs. Their width, shape, and whether they have arms can affect how much usable space each guest has.`,
      ],
    },
    {
      heading: "2. Comfortable Seating Is Different From Maximum Capacity",
      content: [
        `One of the easiest mistakes in wedding seating is confusing how many people fit with how many people should sit there.`,
        `Maximum capacity describes what can technically be accommodated.`,
        `Comfortable capacity considers whether guests still have enough room for:`,
        {
          type: "list",
          items: [
            `their place setting`,
            `glasses and cutlery`,
            `their elbows and personal space`,
            `the centerpiece or other table decoration`,
            `getting in and out of their seats`,
          ],
        },
        `A table that can physically accommodate ten people may feel very different with eight guests, particularly during a long dinner.`,
        `For a wedding reception, where guests may remain at their tables for several hours, comfort is usually more useful than squeezing in the maximum possible number.`,
      ],
    },
    {
      heading: "3. How Many Guests Fit at Common Round Tables?",
      content: [
        `Round tables are popular for wedding receptions because they make conversation easier and use the room differently from long banquet tables.`,
        `As a general starting point, common round-table sizes can work roughly like this:`,
        {
          // The supplied three-column table, rendered as a list — see the
          // structural note at the top of this file. Each item carries its
          // diameter and both column values, unchanged.
          type: "list",
          items: [
            `120 cm / 48 in — comfortable starting point: 4–6 guests. Possible maximum: 6–8 guests.`,
            `150 cm / 60 in — comfortable starting point: around 8 guests. Possible maximum: around 10 guests.`,
            `180 cm / 72 in — comfortable starting point: around 10 guests. Possible maximum: around 12 guests.`,
          ],
        },
        `These are planning ranges, not universal rules.`,
        `The exact capacity depends on the tables and chairs supplied by your venue or rental company, as well as the setup for your reception. If your venue recommends a different capacity, their specification should take priority.`,
        `The distinction between a comfortable starting point and a maximum is important. Two tables with the same diameter can have different practical capacities depending on the chairs, place settings, and service setup.`,
      ],
    },
    {
      heading: "4. What About Rectangular Wedding Tables?",
      content: [
        `Rectangular and banquet tables need a different approach.`,
        `Their length affects how many guests can sit along each side, while the ends may or may not be used depending on the table design and layout.`,
        `A longer table can therefore accommodate more guests, but that does not mean you should simply calculate capacity from its length.`,
        `Consider:`,
        {
          type: "list",
          items: [
            `whether guests will sit at both ends`,
            `the width of the table`,
            `the width of the chairs`,
            `how much space is needed behind each chair`,
            `whether place settings or decorations reduce usable space`,
            `how the table fits within the reception room`,
          ],
        },
        `If your venue is using rectangular or banquet tables, ask for the recommended seating capacity for the exact table configuration rather than relying on a generic number.`,
      ],
    },
    {
      heading: "5. Your Chairs Matter More Than You Might Expect",
      content: [
        `The table is only half of the equation.`,
        `A narrow chair without arms takes up a different amount of space from a wide upholstered chair with arms. Chairs also need enough room behind them so guests can pull them out and sit down without interfering with another table.`,
        `This matters especially when tables are placed close together.`,
        `If you are working with a venue layout, look at the complete setup rather than the table alone:`,
        `table + chairs + place settings + space around the table`,
        `That is what determines whether a seating arrangement will actually work.`,
      ],
    },
    {
      heading: "6. Your Place Settings and Centerpieces Take Up Space",
      content: [
        `A table for eight guests does not always have the same usable space.`,
        `A minimal dinner setup leaves a different amount of room from a formal place setting with chargers, several glasses, cutlery, menus, favors, and a large centerpiece.`,
        `Think about what will actually be on the table.`,
        `If the reception includes elaborate centerpieces or a lot of tableware, leaving a little more room per guest can make the table feel considerably more comfortable.`,
        `This is another reason not to treat a seating number as a fixed rule.`,
      ],
    },
    {
      heading: "7. Does the Meal Style Change the Answer?",
      content: [
        `It can.`,
        `Guests eating a plated dinner may spend a substantial amount of time sitting at their tables. A family-style meal may require serving dishes to be placed within reach of guests. A buffet may reduce some of the food on the table but means guests will be getting up throughout the meal.`,
        `The important question is not whether one meal style automatically requires a particular number of seats.`,
        `It is:`,
        `What will guests need to do at the table?`,
        `The more time guests spend eating, talking, and sharing space at the table, the more valuable comfortable spacing becomes.`,
      ],
    },
    {
      heading: "8. How Many Tables Do You Need?",
      content: [
        `Once you know the comfortable capacity of your tables, you can estimate how many tables you need.`,
        `For example:`,
        `120 reception guests ÷ 8 guests per table = 15 tables`,
        `But there is an important detail here:`,
        `Use your confirmed reception guest count, not the number of people on your original guest list.`,
        `Some invited guests may not attend. Others may attend the ceremony but not the reception. Your seating plan should be based on the people who will actually be seated at the reception.`,
        `This is why the seating process works best after you have a reasonably clear picture of your RSVP responses.`,
        {
          type: "p",
          text: [
            `Working from those replies is the subject of the companion guide, `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
      ],
    },
    {
      heading: "9. What If the Last Table Isn't Full?",
      content: [
        `You will not always end up with perfectly even tables.`,
        `Imagine you have 86 confirmed reception guests and tables designed for eight to ten people. You may end up with a smaller final table if you simply divide the total number of guests.`,
        `That does not automatically mean you should force guests into different groups just to make every table identical.`,
        `Instead, look at the people who still need seats.`,
        `Ask:`,
        {
          type: "list",
          items: [
            `Who already knows each other?`,
            `Which guests are comfortable socializing together?`,
            `Are there couples or family members who should stay together?`,
            `Are there guests who would benefit from being seated with people they already know?`,
            `Are there combinations that you would rather avoid?`,
          ],
        },
        `A slightly smaller table can be perfectly reasonable if the people sitting there make sense together.`,
      ],
    },
    {
      heading: "10. Don't Choose Table Size in Isolation",
      content: [
        `Your table capacity is only one part of the reception layout.`,
        `The final arrangement also depends on the room itself.`,
        `A table that comfortably seats ten guests may still be the wrong choice if fitting enough of those tables into the room leaves too little space between them.`,
        `Consider the whole environment:`,
        `table size → guest capacity → room layout → service → movement`,
        `Guests need to be able to sit down, get up, move between tables, and interact with the rest of the reception without feeling crowded.`,
        `Your venue or planner should be able to confirm the practical layout for the specific room.`,
      ],
    },
    {
      heading: "11. A Practical Rule of Thumb",
      content: [
        `If you need a starting point, 8–10 guests per round wedding table is a useful range for many receptions.`,
        `But don't stop at the number.`,
        `Start with the comfortable capacity recommended for your venue's actual tables, then adjust your plan based on:`,
        {
          type: "list",
          items: [
            `table size and shape`,
            `chair size`,
            `place settings`,
            `centerpieces`,
            `meal style`,
            `room layout`,
            `the groups of people you need to seat`,
          ],
        },
        `The goal is not to fit the largest possible number of guests around every table.`,
        `The goal is to create tables where people can sit comfortably and enjoy the reception together.`,
      ],
    },
    {
      heading: "12. From Table Capacity to a Real Seating Plan",
      content: [
        `Knowing that a table can seat eight people is only the beginning.`,
        `The harder question is:`,
        `Which eight people should sit there?`,
        `Once you know your table capacity, you still need to organize guests into groups, decide who should sit together, place individuals at each table, and review the plan for potential problems.`,
        `A practical seating process therefore looks something like:`,
        `confirmed reception guests → table capacity → groups → individual seats → review`,
        `That is where the seating plan becomes more than a calculation.`,
        {
          type: "p",
          text: [
            `For a step-by-step approach, read `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
        {
          // The one product claim this guide is allowed to make, and the only
          // description of Agapaé in it. See the header note.
          type: "note",
          text: [
            `If you are using Agapaé, its seating tools are available on the Heirloom plan. The guide above explains the planning process; the `,
            {
              text: "Agapaé wedding seating experience",
              href: "/wedding-seating",
            },
            ` shows how seating works in the platform.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the first guide: Google restricts FAQ rich results to government and
  // health sites, so the markup would buy nothing while committing us to a
  // second copy of every answer.
  faq: [
    {
      q: `How many guests should sit at a round wedding table?`,
      a: `For many wedding receptions, 8–10 guests is a useful starting range for a round table. The appropriate number depends on the table's diameter, chairs, place settings, decorations, and the venue's recommended capacity.`,
    },
    {
      q: `Is 10 people too many for a wedding table?`,
      a: `Not necessarily. Ten guests can work comfortably on a sufficiently large table with an appropriate setup. Whether ten is comfortable depends on the table size, chairs, place settings, and the amount of space available around the table.`,
    },
    {
      q: `How many people fit at a 60-inch round table?`,
      a: `A 60-inch (150 cm) round table is commonly planned for around eight guests, with around ten sometimes possible depending on the chairs and reception setup. Your venue or rental provider's recommendation should take priority.`,
    },
    {
      q: `How many people fit at a 72-inch round table?`,
      a: `A 72-inch (180 cm) round table can commonly accommodate around ten guests comfortably, with more possible depending on the exact setup. The practical capacity should be confirmed with your venue or rental provider.`,
    },
    {
      q: `Should every wedding table have the same number of guests?`,
      a: `No. Tables do not have to contain exactly the same number of guests. A slightly smaller table can work well if the guests seated together make sense as a group. Trying to make every table identical can sometimes create less natural groupings.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // Deliberately empty, matching the first guide. The companion guide is
    // already linked twice from the prose, at the two points where a reader
    // actually needs it; a third link under this heading would be a link built
    // for a crawler rather than for the person reading.
    guides: [],

    product: [
      {
        href: "/wedding-seating",
        label: "Wedding seating",
        blurb:
          "See how seating works in Agapaé. Seating tools are available on the Heirloom plan.",
      },
    ],
  },
};

export default howManyPeopleWeddingTableEn;
